import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { QueryStatus } from "../../../utils";
import { RootState } from "../../../redux/store";
import {
  GetHomeChargingSupportingDocumentsPayload,
  HomeChargingSupportingDocument,
  HomeChargingSupportingDocumentsToCreate,
} from "./homeChargingSupportingDocumentsAPI";
import {
  fakeHomeChargingSupportingDocumentsByMonth,
  fakeHomeChargingSupportingDocumentsMonths,
} from "./homeChargingSupportingDocumentsFakeData";

export interface HomeChargingSupportingDocumentsState {
  homeChargingSupportingDocumentsStatus: QueryStatus;
  homeChargingSupportingDocuments: HomeChargingSupportingDocument[];
  homeChargingSupportingDocumentsPeriod:
    | GetHomeChargingSupportingDocumentsPayload
    | undefined;
  createHomeChargingSupportingDocumentsStatus: QueryStatus;
  homeChargingSupportingDocumentsMonthsStatus: QueryStatus;
  homeChargingSupportingDocumentsMonths: string[];
}

const initialState: HomeChargingSupportingDocumentsState = {
  homeChargingSupportingDocumentsStatus: "idle",
  homeChargingSupportingDocuments: [],
  homeChargingSupportingDocumentsPeriod: undefined,
  createHomeChargingSupportingDocumentsStatus: "idle",
  homeChargingSupportingDocumentsMonthsStatus: "idle",
  homeChargingSupportingDocumentsMonths: [],
};

export const getHomeChargingSupportingDocumentsAsync = createAsyncThunk(
  "getHomeChargingSupportingDocumentsAsync/call",
  (payload: GetHomeChargingSupportingDocumentsPayload) => {
    return (
      fakeHomeChargingSupportingDocumentsByMonth[
        new Date(payload.from).getMonth()
      ] ?? []
    );
  },
);

export const getHomeChargingSupportingDocumentsMonthsAsync = createAsyncThunk(
  "getHomeChargingSupportingDocumentsMonths/call",
  () => {
    return fakeHomeChargingSupportingDocumentsMonths;
  },
);

export const createHomeChargingSupportingDocumentsAsync = createAsyncThunk(
  "createHomeChargingSupportingDocuments/call",
  (supportingDocumentsToCreate: HomeChargingSupportingDocumentsToCreate) => {
    return supportingDocumentsToCreate;
  },
);

export const homeChargingSupportingDocumentsSlice = createSlice({
  name: "homeChargingSupportingDocuments",
  initialState,
  reducers: {
    setHomeChargingSupportingDocumentsPeriod(
      state,
      action: PayloadAction<
        GetHomeChargingSupportingDocumentsPayload | undefined
      >,
    ) {
      state.homeChargingSupportingDocumentsPeriod = action.payload;
    },
    resetHomeChargingSupportingDocumentsStatus(state) {
      state.homeChargingSupportingDocumentsStatus = "idle";
    },
    resetCreateHomeChargingSupportingDocumentsStatus(state) {
      state.createHomeChargingSupportingDocumentsStatus = "idle";
    },
    resetHomeChargingSupportingDocumentsMonthsStatus(state) {
      state.homeChargingSupportingDocumentsMonthsStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getHomeChargingSupportingDocumentsAsync.pending, (state) => {
        state.homeChargingSupportingDocumentsStatus = "processing";
      })
      .addCase(
        getHomeChargingSupportingDocumentsAsync.fulfilled,
        (state, action) => {
          state.homeChargingSupportingDocumentsStatus = "success";
          state.homeChargingSupportingDocuments = action.payload;
        },
      )
      .addCase(getHomeChargingSupportingDocumentsAsync.rejected, (state) => {
        state.homeChargingSupportingDocumentsStatus = "failed";
      })
      .addCase(createHomeChargingSupportingDocumentsAsync.pending, (state) => {
        state.createHomeChargingSupportingDocumentsStatus = "processing";
      })
      .addCase(
        createHomeChargingSupportingDocumentsAsync.fulfilled,
        (state) => {
          state.createHomeChargingSupportingDocumentsStatus = "success";
        },
      )
      .addCase(createHomeChargingSupportingDocumentsAsync.rejected, (state) => {
        state.createHomeChargingSupportingDocumentsStatus = "failed";
      })
      .addCase(
        getHomeChargingSupportingDocumentsMonthsAsync.pending,
        (state) => {
          state.homeChargingSupportingDocumentsMonthsStatus = "processing";
        },
      )
      .addCase(
        getHomeChargingSupportingDocumentsMonthsAsync.fulfilled,
        (state, action) => {
          state.homeChargingSupportingDocumentsMonthsStatus = "success";
          state.homeChargingSupportingDocumentsMonths = action.payload;
        },
      )
      .addCase(
        getHomeChargingSupportingDocumentsMonthsAsync.rejected,
        (state) => {
          state.homeChargingSupportingDocumentsMonthsStatus = "failed";
        },
      );
  },
});

export const selectHomeChargingSupportingDocumentsStatus = (state: RootState) =>
  state.homeChargingSupportingDocuments.homeChargingSupportingDocumentsStatus;

export const selectHomeChargingSupportingDocuments = (state: RootState) =>
  state.homeChargingSupportingDocuments.homeChargingSupportingDocuments;

export const selectHomeChargingSupportingDocumentsPeriod = (state: RootState) =>
  state.homeChargingSupportingDocuments.homeChargingSupportingDocumentsPeriod;

export const selectCreateHomeChargingSupportingDocumentsStatus = (
  state: RootState,
) =>
  state.homeChargingSupportingDocuments
    .createHomeChargingSupportingDocumentsStatus;

export const selectHomeChargingSupportingDocumentsMonths = (state: RootState) =>
  state.homeChargingSupportingDocuments.homeChargingSupportingDocumentsMonths;

export const selectHomeChargingSupportingDocumentsMonthsStatus = (
  state: RootState,
) =>
  state.homeChargingSupportingDocuments
    .homeChargingSupportingDocumentsMonthsStatus;

export default homeChargingSupportingDocumentsSlice.reducer;
